/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { Amounts, TalerError } from "@gnu-taler/taler-util";
import {
  Loading,
  RenderAmount,
  RouteDefinition,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { RetryableError } from "../../components/RetryableError.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";

export function AdminAccountSummary({
  routeTransfer,
}: {
  routeTransfer: RouteDefinition;
}): VNode {
  const { i18n } = useTranslationContext();
  const { config } = useBankCoreApiContext();
  const result = useAccountDetails("admin");
  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return (
      <RetryableError
        error={result}
        title={i18n.str`Failed to load the admin account.`}
        onRetry={() => void revalidateAccountDetails()}
      />
    );
  }
  if (result.type === "fail") return <div />;
  return (
    <section
      class="mt-8 rounded-lg border border-outlineVariant p-5"
      aria-labelledby="admin-account-heading"
    >
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2
            id="admin-account-heading"
            class="font-semibold text-onBackground"
          >
            <i18n.Translate>Admin account</i18n.Translate>
          </h2>
          <p class="mt-1 text-2xl font-bold">
            <RenderAmount
              value={Amounts.parseOrThrow(result.body.balance.amount)}
              negative={result.body.balance.credit_debit_indicator === "debit"}
              withSign
              spec={config.currency_specification}
            />
          </p>
        </div>
        <a
          href={routeTransfer.url({})}
          class="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary hover:bg-primaryHover hover:text-white"
        >
          <i18n.Translate>Send bank transfer</i18n.Translate>
        </a>
      </div>
    </section>
  );
}
